import React, { createContext, useState, useEffect, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import { addNotification, getNotificationCount } from '../redux/notification/notification';

const WebSocketContext = createContext();

export const useWebSocket = () => useContext(WebSocketContext);

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || (
    process.env.NODE_ENV === "development"
        ? "http://localhost:4000"
        : "https://api.electrowiz.in"
);

export const WebSocketProvider = ({ children }) => {
    const dispatch = useDispatch();
    const [socket, setSocket] = useState(null);
    const [isConnected, setIsConnected] = useState(false);

    const user = useSelector((state) => state.user);
    const userId = user?.user?._id;

    useEffect(() => {
        if (!userId) return;

        dispatch(getNotificationCount());

        const newSocket = io(SOCKET_URL, {
            // Polling first, then upgrade: if the server refuses websocket the connection stays on polling
            transports: ["polling", "websocket"],
            upgrade: true,
            reconnection: true,
            reconnectionDelay: 2000,
        });
        setSocket(newSocket);

        newSocket.on("connect", () => {
            setIsConnected(true);
            // Re-register on every (re)connect so the server maps this socket to this admin
            newSocket.emit("register", userId);
        });

        newSocket.on("disconnect", () => {
            setIsConnected(false);
        });

        newSocket.on("connect_error", (error) => {
            console.log("Socket.IO connection error:", error?.message);
        });

        newSocket.on("notification", (notification) => {
            if (notification?.recipient && String(notification.recipient) !== String(userId)) return;
            dispatch(addNotification(notification));
            dispatch(getNotificationCount());
            if (notification?.message || notification?.title) {
                toast.info(notification.message || notification.title);
            }
        });

        return () => {
            newSocket.removeAllListeners();
            newSocket.disconnect();
            setSocket(null);
            setIsConnected(false);
        };
    }, [userId, dispatch]);

    return (
        <WebSocketContext.Provider value={{ socket, isConnected }}>
            {children}
        </WebSocketContext.Provider>
    );
};
